import React, { useCallback, useEffect, useState } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Icon } from "~/react/ui/icon"
import { Skeleton } from "~/react/ui/skeleton"
import { Card } from "~/react/ui/card"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { useCustomEventListener } from "~/react/hooks/useCustomEventListener"
import { Button } from "~/react/ui/button"
import { campaigns_inbox_index_path, campaigns_queue_index_path, campaigns_path } from "@/lib/routes"

const statBlockVariants = cva("", {
  variants: {
    colorVariant: {
      default: "",
      sent: "stat-block--sent",
      opens: "stat-block--opens",
      replies: "stat-block--replies",
    },
  },
  defaultVariants: {
    colorVariant: "default",
  },
})

interface MetricsData {
  active_campaigns: number
  sent_today: number
  scheduled: number
}

interface CampaignsMetricsProps {
  endpoint: string
  refreshInterval?: number // milliseconds, optional
}

interface StatBlockProps extends VariantProps<typeof statBlockVariants> {
  icon: string
  label: string
  value: number
  suffix: string
  link?: string
  linkLabel?: string
}

const StatBlock: React.FC<StatBlockProps> = ({
  icon,
  label,
  value,
  suffix,
  colorVariant,
  link,
  linkLabel,
}) => {
  return (
    <Card variant="stat" className={statBlockVariants({ colorVariant })}>
      <header className="stat-block__heading mb-6">
        <Icon icon={icon} size="sm" className="stat-block__icon" />
        <h2 className="text-sm-medium text-body-secondary m-0">{label}</h2>
      </header>
      <div className="stat-block__metrics">
        <strong className="stat-block__value text-2xl-medium">
          {CurrencyHelper.numberWithDelimiter(value)}
        </strong>
        <span className="stat-block__precision fw-normal">{suffix}</span>

        {link && linkLabel && (
          <Button asChild size="xs" className="ml-auto">
            <a href={link}>{linkLabel}</a>
          </Button>
        )}
      </div>
    </Card>
  )
}

const StatBlockSkeleton: React.FC = () => (
  <Card variant="stat">
    <header className="stat-block__heading mb-6">
      <Skeleton
        size="freeform"
        style={{ width: "40%", height: "16px" }}
      />
    </header>
    <div className="stat-block__metrics">
      <Skeleton
        size="freeform"
        style={{ width: "20px", height: "20px" }}
      />
      <Skeleton
        size="freeform"
        style={{ width: "50%", height: "20px" }}
      />
    </div>
  </Card>
)

const MetricsSkeleton: React.FC = () => (
  <>
    <StatBlockSkeleton />
    <StatBlockSkeleton />
    <StatBlockSkeleton />
    <StatBlockSkeleton />
  </>
)

const CampaignsMetrics: React.FC<CampaignsMetricsProps> = ({
  endpoint,
  refreshInterval,
}) => {
  const [data, setData] = useState<MetricsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [unreadReplies, setUnreadReplies] = useState<number | null>(null)

  useEffect(() => {
    const fetchMetrics = () => {
      fetch(endpoint, {
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP error ${res.status}`)
          return res.json()
        })
        .then(setData)
        .catch((error) => {
          console.error("Failed to fetch campaign metrics:", error)
        })
        .finally(() => setLoading(false))
    }

    fetchMetrics()

    if (refreshInterval && refreshInterval > 0) {
      const interval = setInterval(fetchMetrics, refreshInterval)
      return () => clearInterval(interval)
    }
  }, [endpoint, refreshInterval])

  const handleUnreadRepliesUpdate = useCallback(
    (event: CustomEvent<{ count: number }>) => {
      setUnreadReplies(event.detail.count)
    },
    []
  )

  useCustomEventListener("unread-replies:update", handleUnreadRepliesUpdate)

  if (loading || !data) {
    return <MetricsSkeleton />
  }

  const activeCampaignsSuffix =
    data.active_campaigns === 1
      ? i18n.t("js.campaigns.one")
      : i18n.t("js.campaigns.other")
  const emailsSuffix =
    data.sent_today === 1 ? i18n.t("js.email") : i18n.t("js.emails")
  const scheduledSuffix =
    data.scheduled === 1 ? i18n.t("js.email") : i18n.t("js.emails")

  return (
    <>
      <StatBlock
        icon="paper-plane"
        label={i18n.t("js.campaigns.overview.active")}
        value={data.active_campaigns}
        suffix={activeCampaignsSuffix}
        colorVariant="opens"
        link={campaigns_path({ filter: "active" })}
        linkLabel={i18n.t("js.campaigns.overview.view")}
      />
      {unreadReplies !== null ? (
        <StatBlock
          icon="share"
          label={i18n.t("js.campaigns.overview.replies")}
          value={unreadReplies}
          suffix={i18n.t("js.campaigns.overview.replies_unread")}
          colorVariant="replies"
          link={campaigns_inbox_index_path({ read: false })}
          linkLabel={i18n.t("js.campaigns.overview.go_to_inbox")}
        />
      ) : (
        <StatBlockSkeleton />
      )}
      <StatBlock
        icon="envelope"
        label={i18n.t("js.campaigns.overview.sent")}
        value={data.sent_today}
        suffix={emailsSuffix}
        colorVariant="sent"
      />
      <StatBlock
        icon="hourglass"
        label={i18n.t("js.campaigns.overview.scheduled")}
        value={data.scheduled}
        suffix={scheduledSuffix}
        link={campaigns_queue_index_path()}
        linkLabel={i18n.t("js.campaigns.overview.go_to_queue")}
      />
    </>
  )
}

export default CampaignsMetrics
